import React, { createContext, FC, memo, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DeviceEdgeModuleDto, Equipment, ISiteEquipment, PropertyState, PropertyType } from 'shared/api/api';
import { AsyncOperationHandler } from 'shared/helpers/redux-helpers';
import { getDesiredPropertyValue, getIpAddressKey } from './equipment-information/equipment-information-helpers';
import { showErrorSnackbar } from 'shared/state/ducks/snackbar/snackbar-slice';
import { localized } from 'shared/state/i18n/i18n';

export interface OwnerFields {
	name?: string;
	address?: string;
	postNumber?: string;
	city?: string;
	cvrNr?: string;
}

export interface SiteFields {
	chrNr?: string;
	addresse?: string;
	postNr?: string;
	city?: string;
	name?: string;
	country?: string;
}

interface EquipmentActions {
	equipments: ISiteEquipment[];
	addEquipment: (equipment: ISiteEquipment) => void;
	removeEquipment: (equipment: ISiteEquipment) => void;
	updateEquipment: (equipment: ISiteEquipment) => void;
	clearEquipments: () => void;
	pendingDeleteCodes: string[];
	markEquipmentForDeletion: (equipment: ISiteEquipment) => void;
	clearPendingDeletes: () => void;
	equipmentCodeToId: Record<string, string>;
}

interface FieldsContext<T> {
	fields: T;
	setField: <K extends keyof T>(key: K, value: T[K]) => void;
	clearFields: () => void;
}

interface EditContextValue {
	editingSiteId: string | undefined;
	editingProcessEquipment: string | undefined;
	isLoadingEdit: boolean;
	startEditing: (siteId: string) => Promise<void>;
	stopEditing: () => void;
	startEditingProcessEquipment: (processEquipmentId: string) => void;
	stopEditingProcessEquipment: () => void;
}

const OwnerContext = createContext<FieldsContext<OwnerFields> | undefined>(undefined);
const SiteContext = createContext<FieldsContext<SiteFields> | undefined>(undefined);
const EquipmentFormContext = createContext<FieldsContext<Partial<ISiteEquipment>> | undefined>(undefined);
const EquipmentsContext = createContext<EquipmentActions | undefined>(undefined);
const EditContext = createContext<EditContextValue | undefined>(undefined);

export const useOwnerContext = () => {
	const ctx = useContext(OwnerContext);
	if (!ctx) throw new Error('useOwnerContext must be used within CreateSiteProvider');
	return ctx;
};

export const useSiteContext = () => {
	const ctx = useContext(SiteContext);
	if (!ctx) throw new Error('useSiteContext must be used within CreateSiteProvider');
	return ctx;
};

export const useProcessEquipmentContext = () => {
	const ctx = useContext(EquipmentFormContext);
	if (!ctx) throw new Error('useProcessEquipmentContext must be used within CreateSiteProvider');
	return ctx;
};

export const useEquipmentsContext = () => {
	const ctx = useContext(EquipmentsContext);
	if (!ctx) throw new Error('useEqupmentsContext must be used within CreateSiteProvider');
	return ctx;
};

export const useEditContext = () => {
	const ctx = useContext(EditContext);
	if (!ctx) throw new Error('useEditContext must be used within CreateSiteProvider');
	return ctx;
};

interface Props {
	children: ReactNode;
}

export const CreateSiteProvider: FC<Props> = memo(({ children }) => {
	const [ownerFields, setOwnerFields] = useState<OwnerFields>({});
	const [siteFields, setSiteFields] = useState<SiteFields>({});
	const [processEquipmentFields, setProcessEquipmentFields] = useState<Partial<ISiteEquipment>>({});
	const [equipments, setEquipments] = useState<ISiteEquipment[]>([]);
	const [pendingDeleteCodes, setPendingDeleteCodes] = useState<string[]>([]);
	const [equipmentCodeToId, setEquipmentCodeToId] = useState<Record<string, string>>({});
	const [editingSiteId, setEditingSiteId] = useState<string | undefined>(undefined);
	const [editingProcessEquipment, setEditingProcessEquipment] = useState<string>();
	const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);
	const dispatch = useDispatch();

	const setOwnerField = useCallback(<K extends keyof OwnerFields>(key: K, value: OwnerFields[K]) => {
		setOwnerFields(prev => ({ ...prev, [key]: value }));
	}, []);

	const setSiteField = useCallback(<K extends keyof SiteFields>(key: K, value: SiteFields[K]) => {
		setSiteFields(prev => ({ ...prev, [key]: value }));
	}, []);

	const setProcessEquipmentField = useCallback(
		<K extends keyof Partial<ISiteEquipment>>(key: K, value: Partial<ISiteEquipment>[K]) => {
			setProcessEquipmentFields(prev => ({ ...prev, [key]: value }));
		},
		[],
	);

	const clearOwnerFields = useCallback(() => setOwnerFields({}), []);
	const clearSiteFields = useCallback(() => setSiteFields({}), []);
	const clearProcessEquipmentFields = useCallback(() => setProcessEquipmentFields({}), []);
	const clearEquipments = useCallback(() => {
		setEquipments([]);
		setEquipmentCodeToId({});
	}, []);
	const clearPendingDeletes = useCallback(() => setPendingDeleteCodes([]), []);

	const markEquipmentForDeletion = useCallback((equipment: ISiteEquipment) => {
		setEquipments(prev => prev.filter(p => p.code !== equipment.code));
		if (equipment.code) {
			setPendingDeleteCodes(prev => prev.concat(equipment.code!));
		}
	}, []);

	const addProcessEquipment = useCallback((equipement: ISiteEquipment) => {
		setEquipments(prev => prev.concat(equipement));
	}, []);

	const updateProcessEquipment = useCallback((equipement: ISiteEquipment) => {
		setEquipments(prev =>
			prev.map(p => {
				if (p.code === equipement.code) {
					return equipement;
				}
				return p;
			}),
		);
	}, []);

	const removeProcessEquipment = useCallback((equipement: ISiteEquipment) => {
		setEquipments(prev => prev.filter(p => p.code !== equipement.code));
	}, []);

	const stopEditing = useCallback(() => {
		setEditingSiteId(undefined);
		clearProcessEquipmentFields();
	}, [clearProcessEquipmentFields]);

	const startEditingProcessEquipment = useCallback(
		(processEquipmentId: string) => {
			setEditingProcessEquipment(processEquipmentId);

			const chosenEquipment = equipments.find(eq => eq.code === processEquipmentId);
			if (chosenEquipment) {
				setProcessEquipmentFields(chosenEquipment);
			}
		},
		[equipments],
	);

	const stopEditingProcessEquipment = useCallback(() => {
		setEditingProcessEquipment(undefined);
	}, []);

	const startEditing = useCallback(
		async (siteId: string) => {
			setIsLoadingEdit(true);
			try {
				const site = await AsyncOperationHandler(client => client.sites_Get(siteId));
				const equipment = await AsyncOperationHandler(client => client.processEquipment_GetBySiteId(siteId));

				const siteEquipment: ISiteEquipment[] = await Promise.all(
					equipment.map(async eq => {
						const ipAddressKey = getIpAddressKey(eq.equipment);
						const request = DeviceEdgeModuleDto.fromJS({
							code: eq.code,
							equipment: eq.equipment,
							properties: [
								{ propertyName: 'timeZone', state: PropertyState.Desired, type: PropertyType.Text },
								{ propertyName: ipAddressKey, state: PropertyState.Desired, type: PropertyType.IpAddress },
							],
						});

						const proxy = await AsyncOperationHandler(client =>
							client.iotHub_GetDeviceProxyProperties(request),
						).catch(() => undefined);

						return {
							equipment: eq.equipment,
							code: eq.code,
							ipAddress: getDesiredPropertyValue(proxy?.properties, ipAddressKey),
							timeZone: getDesiredPropertyValue(proxy?.properties, 'timeZone'),
						};
					}),
				);
				setEquipments(siteEquipment);
				setEquipmentCodeToId(
					Object.fromEntries(
						equipment.filter(eq => eq.code && eq.id).map(eq => [eq.code!, eq.id!]),
					),
				);

				setOwnerFields({
					name: site.ownerName,
					address: site.ownerAddress,
					postNumber: site.ownerZipCode ? String(site.ownerZipCode) : undefined,
					city: site.ownerCity,
					cvrNr: site.cvRnum ? String(site.cvRnum) : undefined,
				});
				setSiteFields({
					name: site.siteName,
					addresse: site.siteAddress,
					postNr: site.siteZipCode ? String(site.siteZipCode) : undefined,
					city: site.siteCity,
					country: site.countryId,
					chrNr: site.chRnum ? String(site.chRnum) : undefined,
				});
				setEditingSiteId(siteId);
			} catch {
				dispatch(showErrorSnackbar({ msg: localized('SiteLoadFailed'), duration: 5000 }));
				setEditingSiteId(undefined);
			} finally {
				setIsLoadingEdit(false);
			}
		},
		[dispatch],
	);

	const ownerValue = useMemo(
		() => ({ fields: ownerFields, setField: setOwnerField, clearFields: clearOwnerFields }),
		[ownerFields, setOwnerField, clearOwnerFields],
	);
	const siteValue = useMemo(
		() => ({ fields: siteFields, setField: setSiteField, clearFields: clearSiteFields }),
		[siteFields, setSiteField, clearSiteFields],
	);
	const processEquipmentValue = useMemo(
		() => ({
			fields: processEquipmentFields,
			setField: setProcessEquipmentField,
			clearFields: clearProcessEquipmentFields,
		}),
		[processEquipmentFields, setProcessEquipmentField, clearProcessEquipmentFields],
	);

	const equipmentsValue: EquipmentActions = useMemo(() => {
		return {
			equipments: equipments,
			addEquipment: addProcessEquipment,
			removeEquipment: removeProcessEquipment,
			updateEquipment: updateProcessEquipment,
			clearEquipments: clearEquipments,
			pendingDeleteCodes: pendingDeleteCodes,
			markEquipmentForDeletion: markEquipmentForDeletion,
			clearPendingDeletes: clearPendingDeletes,
			equipmentCodeToId: equipmentCodeToId,
		};
	}, [addProcessEquipment, clearEquipments, clearPendingDeletes, equipmentCodeToId, equipments, markEquipmentForDeletion, pendingDeleteCodes, removeProcessEquipment, updateProcessEquipment]);

	const editValue: EditContextValue = useMemo(
		() => ({
			editingSiteId,
			isLoadingEdit,
			startEditing,
			stopEditing,
			editingProcessEquipment,
			startEditingProcessEquipment,
			stopEditingProcessEquipment,
		}),
		[
			editingProcessEquipment,
			editingSiteId,
			isLoadingEdit,
			startEditing,
			startEditingProcessEquipment,
			stopEditing,
			stopEditingProcessEquipment,
		],
	);

	return (
		<EditContext.Provider value={editValue}>
			<OwnerContext.Provider value={ownerValue}>
				<SiteContext.Provider value={siteValue}>
					<EquipmentFormContext.Provider value={processEquipmentValue}>
						<EquipmentsContext.Provider value={equipmentsValue}>{children}</EquipmentsContext.Provider>
					</EquipmentFormContext.Provider>
				</SiteContext.Provider>
			</OwnerContext.Provider>
		</EditContext.Provider>
	);
});
